/**
 * Users Table Component
 *
 * Table display for tenant users with actions.
 *
 * @fileoverview Tenant users data table component
 */

import React from 'react';
import {
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Badge,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  IconButton,
  Box,
  Text,
  Skeleton,
  useColorModeValue,
  Icon,
  Avatar,
  HStack,
} from '@chakra-ui/react';
import {
  MdMoreVert,
  MdEdit,
  MdPersonOff,
  MdPersonAdd,
  MdDelete,
  MdStar,
} from 'react-icons/md';
import type { TenantUser } from '../../types/users';

interface UsersTableProps {
  users: TenantUser[];
  loading: boolean;
  currentUserId?: string;
  onEdit: (user: TenantUser) => void;
  onDeactivate: (user: TenantUser) => void;
  onReactivate: (user: TenantUser) => void;
  onRemove: (user: TenantUser) => void;
}

/**
 * Get role badge for user
 */
function getRoleBadge(user: TenantUser) {
  if (user.isOwner) {
    return { colorScheme: 'purple', label: 'Owner' };
  }
  if (user.tenantRole === 'TENANT_ADMIN') {
    return { colorScheme: 'blue', label: 'Admin' };
  }
  return { colorScheme: 'gray', label: 'Member' };
}

/**
 * Get status badge for user
 */
function getStatusBadge(isActive: boolean) {
  return isActive
    ? { colorScheme: 'green', label: 'Active' }
    : { colorScheme: 'red', label: 'Inactive' };
}

/**
 * Users Table Component
 *
 * Displays tenant users in a table with actions.
 */
export const UsersTable: React.FC<UsersTableProps> = ({
  users,
  loading,
  currentUserId,
  onEdit,
  onDeactivate,
  onReactivate,
  onRemove,
}) => {
  const bgColor = useColorModeValue('white', 'xacto.darkSurface');
  const borderColor = useColorModeValue('gray.200', 'xacto.borderGray');
  const hoverBg = useColorModeValue('gray.50', 'xacto.mutedNavy');
  const textColor = useColorModeValue('gray.900', 'xacto.pureWhite');
  const mutedColor = useColorModeValue('gray.500', 'xacto.silverGray');

  const formatDate = (dateString: string | undefined) => {
    if (!dateString) return 'Never';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const getDisplayName = (user: TenantUser) => {
    if (user.user.displayName) return user.user.displayName;
    if (user.user.firstName || user.user.lastName) {
      return `${user.user.firstName || ''} ${user.user.lastName || ''}`.trim();
    }
    return user.user.email.split('@')[0];
  };

  // Loading skeleton
  if (loading) {
    return (
      <Box bg={bgColor} borderRadius="lg" border="1px" borderColor={borderColor}>
        <Table>
          <Thead>
            <Tr>
              <Th>User</Th>
              <Th>Role</Th>
              <Th>Status</Th>
              <Th>Last Login</Th>
              <Th>Joined</Th>
              <Th>Actions</Th>
            </Tr>
          </Thead>
          <Tbody>
            {[1, 2, 3, 4, 5].map((i) => (
              <Tr key={i}>
                <Td><Skeleton h="40px" /></Td>
                <Td><Skeleton h="20px" w="60px" /></Td>
                <Td><Skeleton h="20px" w="60px" /></Td>
                <Td><Skeleton h="20px" /></Td>
                <Td><Skeleton h="20px" /></Td>
                <Td><Skeleton h="20px" w="40px" /></Td>
              </Tr>
            ))}
          </Tbody>
        </Table>
      </Box>
    );
  }

  // Empty state
  if (users.length === 0) {
    return (
      <Box
        bg={bgColor}
        borderRadius="lg"
        border="1px"
        borderColor={borderColor}
        p={8}
        textAlign="center"
      >
        <Text color={mutedColor}>No users found</Text>
      </Box>
    );
  }

  return (
    <Box bg={bgColor} borderRadius="lg" border="1px" borderColor={borderColor} overflow="hidden">
      <Table>
        <Thead>
          <Tr>
            <Th>User</Th>
            <Th>Role</Th>
            <Th>Status</Th>
            <Th>Last Login</Th>
            <Th>Joined</Th>
            <Th>Actions</Th>
          </Tr>
        </Thead>
        <Tbody>
          {users.map((user) => {
            const role = getRoleBadge(user);
            const status = getStatusBadge(user.user.isActive);
            const isCurrentUser = user.userId === currentUserId;

            return (
              <Tr
                key={user.id}
                _hover={{ bg: hoverBg }}
              >
                <Td>
                  <HStack spacing={3}>
                    <Avatar
                      size="sm"
                      name={getDisplayName(user)}
                    />
                    <Box>
                      <HStack>
                        <Text fontWeight="medium" color={textColor}>
                          {getDisplayName(user)}
                        </Text>
                        {user.isOwner && (
                          <Icon as={MdStar} color="yellow.400" boxSize={4} />
                        )}
                        {isCurrentUser && (
                          <Badge colorScheme="teal" fontSize="xs">You</Badge>
                        )}
                      </HStack>
                      <Text fontSize="sm" color={mutedColor}>
                        {user.user.email}
                      </Text>
                    </Box>
                  </HStack>
                </Td>
                <Td>
                  <Badge colorScheme={role.colorScheme} variant="subtle">
                    {role.label}
                  </Badge>
                </Td>
                <Td>
                  <Badge colorScheme={status.colorScheme} variant="subtle">
                    {status.label}
                  </Badge>
                </Td>
                <Td>
                  <Text fontSize="sm" color={mutedColor}>
                    {formatDate(user.user.lastLoginAt)}
                  </Text>
                </Td>
                <Td>
                  <Text fontSize="sm" color={mutedColor}>
                    {formatDate(user.createdAt)}
                  </Text>
                </Td>
                <Td onClick={(e) => e.stopPropagation()}>
                  <Menu>
                    <MenuButton
                      as={IconButton}
                      icon={<Icon as={MdMoreVert} />}
                      variant="ghost"
                      size="sm"
                      isDisabled={user.isOwner || isCurrentUser}
                    />
                    <MenuList>
                      <MenuItem
                        icon={<Icon as={MdEdit} />}
                        onClick={() => onEdit(user)}
                      >
                        Edit Role
                      </MenuItem>
                      {user.user.isActive ? (
                        <MenuItem
                          icon={<Icon as={MdPersonOff} />}
                          onClick={() => onDeactivate(user)}
                        >
                          Deactivate
                        </MenuItem>
                      ) : (
                        <MenuItem
                          icon={<Icon as={MdPersonAdd} />}
                          onClick={() => onReactivate(user)}
                        >
                          Reactivate
                        </MenuItem>
                      )}
                      <MenuItem
                        icon={<Icon as={MdDelete} />}
                        color="red.500"
                        onClick={() => onRemove(user)}
                      >
                        Remove from Tenant
                      </MenuItem>
                    </MenuList>
                  </Menu>
                </Td>
              </Tr>
            );
          })}
        </Tbody>
      </Table>
    </Box>
  );
};

export default UsersTable;
